/**
 * Model Audit Mode — pure helpers shared by the qa-detail-v3 audit panel and
 * the /model/audit queue page. Mirrors the annotation-review helper module:
 * no DOM, no React, no window — unit-tested from a server vitest file
 * (server/__tests__/model-audit-helpers.test.ts).
 *
 * Activation: `?audit=1&date=YYYY-MM-DD` on /qa/delivery/:id, admin only
 * (the page ANDs this with isAdmin, same as review mode).
 */

import type { AuditModelSnapshot } from "@shared/model-audit";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True iff `search` (with or without leading `?`) contains `audit=1`.
 * Exact match — `audit=true` / `audit=0` are false, like review mode. */
export function isAuditMode(search: string): boolean {
  if (!search) return false;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.get("audit") === "1";
}

/** The audit day from the query string, or "" when absent/malformed. */
export function parseAuditDate(search: string): string {
  if (!search) return "";
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const date = params.get("date") ?? "";
  return DATE_RE.test(date) ? date : "";
}

/** Query string that keeps audit mode + the day across navigation. */
export function buildAuditSearch(date: string): string {
  const p = new URLSearchParams();
  p.set("audit", "1");
  if (date) p.set("date", date);
  return p.toString();
}

export function auditNeighborsUrl(deliveryId: string, date: string): string {
  const p = new URLSearchParams({ date, id: deliveryId });
  return `/api/admin/model-audit/neighbors?${p.toString()}`;
}

export function auditFindingUrl(deliveryId: string): string {
  return `/api/admin/model-audit/finding/${deliveryId}`;
}

/** Detail-page URL for a delivery in audit mode. */
export function auditDeliveryPath(deliveryId: string, date: string): string {
  return `/qa/delivery/${deliveryId}?${buildAuditSearch(date)}`;
}

/** Pulls the six model values out of the delivery API payload
 * (camelCase fields from GET /api/qa/deliveries/:id). */
export function snapshotFromDelivery(delivery: {
  expectedStreetNumber?: string | null;
  expectedUnitNumber?: string | null;
  expectedDzeCode?: string | null;
  actualStreetNumber?: string | null;
  actualUnitNumber?: string | null;
  actualDzeCode?: string | null;
}): AuditModelSnapshot {
  return {
    expected: {
      streetNumber: delivery.expectedStreetNumber ?? null,
      unitNumber: delivery.expectedUnitNumber ?? null,
      dzeCode: delivery.expectedDzeCode ?? null,
    },
    actual: {
      streetNumber: delivery.actualStreetNumber ?? null,
      unitNumber: delivery.actualUnitNumber ?? null,
      dzeCode: delivery.actualDzeCode ?? null,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Keyboard shortcuts — nav only (verdicts are clicked; with 24 possible
// cells, key-per-verdict would be error-prone). Same event-filtering rules
// as review mode: no modifiers, not inside form fields.
// ─────────────────────────────────────────────────────────────────────────────

export type AuditAction = "next" | "prev";

interface KeyEventLike {
  key: string;
  target: { tagName?: string; isContentEditable?: boolean } | null;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

export function keyToAuditAction(ev: KeyEventLike): AuditAction | null {
  if (ev.ctrlKey || ev.metaKey || ev.altKey) return null;
  const tag = (ev.target?.tagName ?? "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return null;
  if (ev.target?.isContentEditable) return null;
  switch (ev.key) {
    case "j":
    case "ArrowRight":
      return "next";
    case "k":
    case "ArrowLeft":
      return "prev";
    default:
      return null;
  }
}
